import {getTextFieldValidationMessage} from '@github-ui/memex-field-validators'
import {memo, useCallback} from 'react'

import {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import type {EnrichedText} from '../../../api/columns/contracts/text'
import {replaceShortCodesWithEmojis} from '../../../helpers/emojis'
import {useUpdateItem} from '../../../hooks/use-update-item'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {RepoPicker} from '../../repo-picker'
import {useTableNavigationFocusInitialValue} from '../navigation'
import {TextRenderer} from '../renderers'
import {TextCellEditor} from './text-cell-editor'
import {useConvertToIssueHelper} from './use-convert-to-issue-helper'
import {useSummaryAnchor} from './use-summary-anchor'

type Props = Readonly<{
  currentValue: ColumnValue<EnrichedText>
  columnId: number
  rowId: string
  replaceContents: boolean
  model: MemexItemModel
  isIssueField: boolean
}>

export const TextEditor: React.FC<Props> = memo(function TextEditor({
  currentValue,
  model,
  rowId,
  columnId,
  isIssueField,
  replaceContents,
}) {
  const initialValue = useTableNavigationFocusInitialValue()
  const {showConvertToIssue, onRowConvertSuccess} = useConvertToIssueHelper({model, columnId, isIssueField})
  const renderButton = useCallback(() => {
    return <TextRenderer currentValue={currentValue} model={model} />
  }, [currentValue, model])
  const {renderAnchor, onOpenChange} = useSummaryAnchor({columnId, renderButton})

  const textValue = hasValue(currentValue) ? currentValue.value.raw : ''
  const {updateItem} = useUpdateItem()

  const value = replaceContents || !textValue ? initialValue : textValue

  const handleUpdate = useCallback(
    (nextValue: string) => {
      updateItem(model, {
        dataType: MemexColumnDataType.Text,
        memexProjectColumnId: columnId,
        value: replaceShortCodesWithEmojis(nextValue),
        isIssueField,
      })

      return true
    },
    [columnId, model, updateItem, isIssueField],
  )

  if (showConvertToIssue) {
    return (
      <RepoPicker
        renderAnchor={renderAnchor}
        isOpen
        showPrompt
        item={model}
        onOpenChange={onOpenChange}
        onSuccess={onRowConvertSuccess}
      />
    )
  }

  return (
    <TextCellEditor
      model={model}
      columnId={columnId}
      rowId={rowId}
      defaultValue={value}
      onUpdate={handleUpdate}
      validationFn={getTextFieldValidationMessage}
    />
  )
})

TextEditor.displayName = 'TextEditor'
